import { makeAutoObservable } from 'mobx';
import type CartItem from 'models/CartItem';
import type {
  CateringDraftOrderDetailItem,
  PendingCateringDraftOrderItem,
  PendingCateringDraftOrderItemWithId,
} from 'models/CateringDraftOrder';
import { normalizeCartItemMods } from 'utils/CartHelpers';

// Same shape the standard cart hashes on (Cart.handleAddItemToCart via
// CartItem.hash()): menu_item_id + sorted mods + special_instructions.
// Used so duplicate adds collapse into a single line with summed qty.
function mergeSignature(
  menu_item_id: string,
  mods: ReadonlyArray<{ menu_item_id?: string }>,
  special_instructions: string
): string {
  const sortedMods = [...mods].sort((a, b) => String(a.menu_item_id ?? '').localeCompare(String(b.menu_item_id ?? '')));
  return JSON.stringify({ menu_item_id, mods: sortedMods, special_instructions });
}

export default class CateringDraftOrderEditStore {
  isActive = false;
  draftOrderId = '';
  draftOrderNumber = 0;
  // Captured at startAddingItems time so the menu-page submit can pass it
  // for optimistic concurrency. -1 means "no edit_count captured" — the
  // submitter omits draft_edit_count from the request, which makes the
  // backend skip the conflict check.
  draftEditCount = -1;
  // Each pending item gets a stable client-side _pendingId so React keys
  // survive reordering/removal — using array index as the key would re-bind
  // DOM nodes to the wrong row after a splice.
  pendingItems: PendingCateringDraftOrderItemWithId[] = [];
  private nextPendingId = 1;
  // Desktop panel visibility. Default true (panel shows when entering
  // Add-Items mode); the header toggle button can hide/show it.
  panelVisible = true;

  constructor() {
    makeAutoObservable(this);
  }

  togglePanel() {
    this.panelVisible = !this.panelVisible;
  }

  setPanelVisible(visible: boolean) {
    this.panelVisible = visible;
  }

  // Activates the "add items" flow: user navigates to menu page, adds items, returns to edit page.
  // isActive=true prevents the edit page's cleanup effect from resetting state mid-flow.
  // initiallyVisible defaults true (desktop side panel opens immediately on entry);
  // callers pass false on mobile so the half-sheet doesn't auto-cover the menu.
  startAddingItems(orderId: string, orderNumber: number, draftEditCount: number = -1, initiallyVisible = true) {
    this.setDraftContext(orderId, orderNumber, [], draftEditCount, initiallyVisible);
  }

  // Populates pending items from a rejected draft's existing items so the
  // consumer can resubmit them as a new draft from the restaurant menu.
  startReorderingItems(orderId: string, orderNumber: number, items: CateringDraftOrderDetailItem[]) {
    // Each draft line item represents a single unit; the backend stores qty per line.
    this.setDraftContext(
      orderId,
      orderNumber,
      items.map((item) => ({
        menu_item_id: item.menu_item_id,
        qty: 1,
        pretax_cents: 0,
        tax_cents: 0,
        mods: item.mods,
        special_instructions: item.special_instructions,
        displayName: item.name,
      }))
    );
  }

  addPendingItem(cartItem: CartItem) {
    const json = cartItem.toJSON();
    if (!json.menu_item_id || !json.qty) {
      return;
    }
    const mods = normalizeCartItemMods(json.mods);
    const special_instructions = cartItem.special_instructions || '';
    // Match the standard cart's de-dup behavior: identical menu_item_id,
    // mods, and special_instructions collapse into one line with summed
    // qty. Mirrors Cart.handleAddItemToCart.
    const signature = mergeSignature(json.menu_item_id, mods, special_instructions);
    const existing = this.pendingItems.find(
      (pi) => mergeSignature(pi.menu_item_id, pi.mods, pi.special_instructions) === signature
    );
    if (existing) {
      existing.qty += json.qty;
      return;
    }
    // Prices are zero — the server computes final prices from MenuItem at save time.
    this.pendingItems.push({
      _pendingId: this.nextPendingId++,
      menu_item_id: json.menu_item_id,
      qty: json.qty,
      pretax_cents: 0,
      tax_cents: 0,
      mods,
      special_instructions,
      displayName: json.name || json.name_for_customer || 'Item',
    });
  }

  // Snapshot and return pending items, then reset. The caller (edit page) transfers
  // these into local component state so they survive the store reset.
  finishAddingItems(): PendingCateringDraftOrderItem[] {
    const items = [...this.pendingItems];
    this.resetState();
    return items;
  }

  cancel() {
    this.resetState();
  }

  removePendingItemAt(index: number) {
    if (index >= 0 && index < this.pendingItems.length) {
      this.pendingItems.splice(index, 1);
    }
  }

  // Updates the cached metadata fields without disturbing pendingItems —
  // used after URL-hydration fetches the draft on refresh / deep-link.
  setDraftMetadata(orderNumber: number, draftEditCount: number) {
    this.draftOrderNumber = orderNumber;
    this.draftEditCount = draftEditCount;
  }

  incrementPendingItemAt(index: number) {
    if (index >= 0 && index < this.pendingItems.length) {
      this.pendingItems[index].qty += 1;
    }
  }

  // Mirrors the standard cart's stepper: minus at qty>1 lowers; the trash
  // icon at qty===1 is the path that actually removes the row, so callers
  // must pick the right action. We still guard qty>1 here defensively.
  decrementPendingItemAt(index: number) {
    if (index >= 0 && index < this.pendingItems.length && this.pendingItems[index].qty > 1) {
      this.pendingItems[index].qty -= 1;
    }
  }

  private setDraftContext(
    orderId: string,
    orderNumber: number,
    items: PendingCateringDraftOrderItem[] = [],
    draftEditCount: number = -1,
    panelVisible = true
  ) {
    this.isActive = true;
    this.draftOrderId = orderId;
    this.draftOrderNumber = orderNumber;
    this.draftEditCount = draftEditCount;
    this.pendingItems = items.map((item) => ({ ...item, _pendingId: this.nextPendingId++ }));
    this.panelVisible = panelVisible;
  }

  private resetState() {
    this.isActive = false;
    this.draftOrderId = '';
    this.draftOrderNumber = 0;
    this.draftEditCount = -1;
    this.pendingItems = [];
    this.panelVisible = true;
  }
}
